import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildAuthRequiredRedirectUrl, performWebLogout } from './profileFormUtils';
import './AccountLayout.css';

const DOC_TITLE = 'Password updated · Digi Rasa';
const PASSWORD_UPDATED_STORAGE_KEY = 'digirasa_password_updated';

const DEFAULT_MESSAGE =
  'Your password was updated successfully. Please sign in with your new password.';

function readStashedNotice() {
  try {
    const raw = sessionStorage.getItem(PASSWORD_UPDATED_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PASSWORD_UPDATED_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Public post–password-change / reset confirmation.
 * Expects optional `location.state`: `{ title?, message?, signedOut? }`.
 */
const AccountPasswordUpdated = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stashed] = useState(() => readStashedNotice());

  const { title, message, signedOut } = useMemo(() => {
    const state = location.state && typeof location.state === 'object' ? location.state : {};
    const source = {
      title: state.title || stashed?.title,
      message: state.message || stashed?.message,
      signedOut: state.signedOut === true || stashed?.signedOut === true,
    };
    return {
      title:
        typeof source.title === 'string' && source.title.trim()
          ? source.title.trim()
          : 'Password updated',
      message:
        typeof source.message === 'string' && source.message.trim()
          ? source.message.trim()
          : DEFAULT_MESSAGE,
      signedOut: source.signedOut === true,
    };
  }, [location.state, stashed]);

  useEffect(() => {
    const prev = document.title;
    document.title = DOC_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (!signedOut) return undefined;
    // Defer logout until after this public page has painted, so AccountLayout
    // does not bounce a protected route to /?login=1 first.
    const id = window.setTimeout(() => {
      performWebLogout();
    }, 0);
    return () => window.clearTimeout(id);
  }, [signedOut]);

  const goToLogin = () => {
    navigate(buildAuthRequiredRedirectUrl(undefined, { reauth: true }), { replace: true });
  };

  return (
    <div className="account-page account-page--recovery">
      <div className="account-page__title-block">
        <h1>{title}</h1>
      </div>

      <div
        className="auth-inline-message auth-inline-message--success"
        role="status"
        aria-live="polite"
        style={{ marginBottom: '16px' }}
      >
        {message}
      </div>

      {signedOut ? (
        <p className="account-page__muted" style={{ marginBottom: '16px' }}>
          You are signed out. Sign in to continue with your new password.
        </p>
      ) : null}

      <button type="button" className="auth-form__submit" onClick={goToLogin}>
        Sign in
      </button>

      <p style={{ marginTop: '16px' }}>
        <Link to="/" className="account-layout__back" style={{ marginBottom: 0 }}>
          ← Back to home
        </Link>
      </p>
    </div>
  );
};

export default AccountPasswordUpdated;
